import { Route, Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { permissionGuard } from './guards/permission.guard';
import { LayoutComponent } from './components/layout/layout.component';
import { NAV_SECTIONS } from '@common/constants';

/**
 * Attaches `data.permission`/`data.anyPermission` + `permissionGuard` to every
 * route whose full path matches a gated NAV_SECTIONS item, so the sidebar and
 * the route guard are mechanically derived from one source and can't drift
 * apart. Routes with no matching nav entry (e.g. `profile`, redirect stubs)
 * are left as-is — the guard fails open when a route declares no permission.
 */
function applyNavPermissions(children: Routes, parentPath = ''): Routes {
  const navPermissions = new Map<string, { permission?: string; anyPermission?: string[] }>();
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.path && (item.permission || item.anyPermission)) {
        navPermissions.set(item.path, {
          permission: item.permission,
          anyPermission: item.anyPermission,
        });
      }
    }
  }

  const walk = (routesToWalk: Routes, parent: string): Routes =>
    routesToWalk.map((route) => {
      const fullPath = route.path ? [parent, route.path].filter(Boolean).join('/') : parent;
      const match = navPermissions.get(fullPath);
      const next: Route = { ...route };
      if (match) {
        next.data = { ...next.data, ...match };
        next.canActivate = [...(next.canActivate ?? []), permissionGuard];
      }
      if (route.children) {
        next.children = walk(route.children, fullPath);
      }
      return next;
    });

  return walk(children, parentPath);
}

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./modules/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./modules/forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./modules/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    path: '',
    canActivate: [authGuard],
    component: LayoutComponent,
    children: applyNavPermissions([
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'access-denied',
        loadComponent: () =>
          import('./modules/access-denied/access-denied.page').then((m) => m.AccessDeniedPage),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./modules/profile/profile.page').then((m) => m.UserProfilePage),
      },
      {
        path: 'users',
        loadComponent: () => import('./modules/user/user.page').then((m) => m.UserPage),
      },
      { path: 'kyc/users', redirectTo: 'users', pathMatch: 'full' },
      {
        path: 'kyc',
        children: [
          {
            path: 'types',
            loadComponent: () =>
              import('./modules/kyc-type/kyc-type.page').then((m) => m.KycTypePage),
          },
        ],
      },
      {
        path: 'trips',
        loadComponent: () => import('./modules/trips/trips.page').then((m) => m.TripsPage),
      },
      { path: 'bookings', redirectTo: 'trips', pathMatch: 'full' },
      {
        path: 'audit-trail',
        loadComponent: () =>
          import('./modules/audit-trail/audit-trail.page').then((m) => m.AuditTrailPage),
      },
      {
        path: 'vehicles',
        children: [
          {
            path: 'listings',
            loadComponent: () =>
              import('./modules/listing/listing.page').then((m) => m.ListingPage),
          },
          {
            path: 'catalog',
            loadComponent: () =>
              import('./modules/vehicle-catalog/vehicle-catalog.page').then(
                (m) => m.VehicleCatalogPage,
              ),
          },
          { path: 'brands', redirectTo: '/vehicles/catalog', pathMatch: 'full' },
          { path: 'cars', redirectTo: '/vehicles/catalog', pathMatch: 'full' },
        ],
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./modules/command-dashboard/command-dashboard.page').then(
            (m) => m.CommandDashboardPage,
          ),
      },
      {
        path: 'issues',
        children: [
          {
            path: '',
            loadComponent: () => import('./modules/issue/issue.page').then((m) => m.IssuePage),
          },
          {
            path: 'types',
            loadComponent: () =>
              import('./modules/issue-type/issue-type.page').then((m) => m.IssueTypePage),
          },
          {
            path: 'categories',
            loadComponent: () =>
              import('./modules/issue-category/issue-category.page').then(
                (m) => m.IssueCategoryPage,
              ),
          },
        ],
      },
      {
        path: 'roles',
        loadComponent: () => import('./modules/role/role.page').then((m) => m.RolePage),
      },
      {
        path: 'groups',
        canActivate: [authGuard],
        loadComponent: () => import('./modules/group/group.page').then((m) => m.GroupPage),
      },
      { path: 'telemetry', redirectTo: 'trips', pathMatch: 'full' },
      { path: 'telemetry/:deviceId', redirectTo: 'trips', pathMatch: 'full' },
      // create/edit merged into the map-dominant /zones page itself — no more
      // separate form route, mirroring how bookings/telemetry merged into /trips above.
      { path: 'zones/new', redirectTo: 'zones', pathMatch: 'full' },
      { path: 'zones/:id/edit', redirectTo: 'zones', pathMatch: 'full' },
      {
        path: 'zones',
        children: [
          {
            path: '',
            loadComponent: () => import('./modules/zone/zone.page').then((m) => m.ZonePage),
          },
        ],
      },
    ]),
  },
  {
    path: '**',
    loadComponent: () => import('./modules/not-found/not-found.page').then((m) => m.NotFoundPage),
  },
];
